/**
 * Converts an RGB color value to HSL.
 */
function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h, s, l];
}

/**
 * Converts an HSL color value to RGB.
 */
function hslToRgb(h, s, l) {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/**
 * Converts a Hex color to HSL
 */
function hexToHsl(hex) {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }
  return rgbToHsl(r, g, b);
}

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Recolor an image using a mask and a target hex color.
 */
export const recolorImage = async (originalSrc, maskSrc, targetHex) => {
  try {
    const [originalImg, maskImg] = await Promise.all([
      loadImage(originalSrc),
      loadImage(maskSrc)
    ]);

    const width = originalImg.width;
    const height = originalImg.height;

    // 1. Create the final result canvas (starts with original image)
    const resultCanvas = document.createElement('canvas');
    resultCanvas.width = width;
    resultCanvas.height = height;
    const resultCtx = resultCanvas.getContext('2d');
    resultCtx.drawImage(originalImg, 0, 0, width, height);

    // 2. Create the colorized overlay canvas
    const overlayCanvas = document.createElement('canvas');
    overlayCanvas.width = width;
    overlayCanvas.height = height;
    const overlayCtx = overlayCanvas.getContext('2d');

    // First draw original image
    overlayCtx.drawImage(originalImg, 0, 0, width, height);

    // Pass 1: Overlay (Preserves contrast and texture while pushing lightness slightly)
    overlayCtx.globalCompositeOperation = 'overlay';
    overlayCtx.globalAlpha = 0.4;
    overlayCtx.fillStyle = targetHex;
    overlayCtx.fillRect(0, 0, width, height);

    // Pass 2: Color (Applies the exact hue/saturation)
    overlayCtx.globalCompositeOperation = 'color';
    overlayCtx.globalAlpha = 0.45; // Lowered to prevent the "thick paint" look
    overlayCtx.fillStyle = targetHex;
    overlayCtx.fillRect(0, 0, width, height);
    
    // Pass 3: Soft Light (Brings back deep shadows)
    overlayCtx.globalCompositeOperation = 'soft-light';
    overlayCtx.globalAlpha = 0.3;
    overlayCtx.fillStyle = targetHex;
    overlayCtx.fillRect(0, 0, width, height);

    // Reset alpha
    overlayCtx.globalAlpha = 1.0;

    // 3. Apply the mask to the colorized overlay with a small blur for soft edges
    overlayCtx.globalCompositeOperation = 'destination-in';
    overlayCtx.filter = 'blur(2px)'; // 2px is the perfect middle ground
    overlayCtx.drawImage(maskImg, 0, 0, width, height);

    // 4. Draw the soft-masked colorized hair on top of the original image
    resultCtx.drawImage(overlayCanvas, 0, 0, width, height);

    return resultCanvas.toDataURL('image/jpeg', 0.9);
  } catch (error) {
    console.error('Error generating recolored image:', error);
    return null;
  }
};
